// Modal wyboru istniejacej kalkulacji do skopiowania (flow "Utworz na podstawie istniejacej").
// Domyslnie pokazuje kalkulacje powiazane z productId pozycji oferty (sekcja "Dla tego produktu").
// Po wpisaniu wyszukiwania pokazuje tez "Inne kalkulacje" (LIKE na nazwie).
// Po wyborze + clone otwiera modal pozycji nowej kalkulacji.

import React, { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, TextField, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSnackbar } from 'notistack';

import { WiseModal } from 'components/wise';
import {
  useGetSalesCrmCalculationTemplates,
  usePostSalesCrmOfferCalculationFromTemplate,
  type ICalculationTemplate,
  type ICalculationTemplatePosition
} from 'api';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/ChooseCalculationTemplateModal.module.scss';

interface IChooseCalculationTemplateModalProps {
  open: boolean;
  onClose: () => void;
  /** Callback po pomyslnym sklonowaniu — dostaje id i nazwe nowej kalkulacji. */
  onClonedSuccess: (newCalculation: { id: number; name: string }) => void;
  clientId: number;
  offerId: string;
  positionId: number;
  productName: string;
}

const formatPrice = (raw: number | null | undefined): string => {
  if (raw == null || Number.isNaN(raw)) return '—';
  return `${raw.toFixed(2).replace('.', ',')} zł`;
};

const formatDate = (raw: string | null | undefined): string => {
  if (!raw) return '—';
  try {
    return new Intl.DateTimeFormat('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(raw));
  } catch {
    return raw;
  }
};

const priceTypeLabel = (type: 'UNIT' | 'TOTAL' | null): string =>
  type === 'UNIT' ? '/ szt.' : 'jednorazowo';

/** Tooltip body — lista skladnikow kalkulacji do podgladu. */
const TemplateTooltipBody: FC<{ positions: ICalculationTemplatePosition[] }> = ({ positions }) => (
  <div style={{ minWidth: 240 }}>
    {positions.length === 0 ? (
      <em>Brak składników</em>
    ) : (
      positions.map((p, idx) => (
        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <span>
            <strong>{p.name}</strong>{' '}
            <span style={{ opacity: 0.7, fontSize: 11 }}>{priceTypeLabel(p.priceType)}</span>
          </span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatPrice(p.priceNet)}
            {p.costNet != null && (
              <span style={{ opacity: 0.7, fontSize: 11 }}> (koszt {formatPrice(p.costNet)})</span>
            )}
          </span>
        </div>
      ))
    )}
  </div>
);

const ChooseCalculationTemplateModal: FC<IChooseCalculationTemplateModalProps> = ({
  open,
  onClose,
  onClonedSuccess,
  clientId,
  offerId,
  positionId,
  productName
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [searchInput, setSearchInput] = useState('');
  const [searchActive, setSearchActive] = useState<string | undefined>(undefined);

  const { data, isLoading, isError } = useGetSalesCrmCalculationTemplates(
    clientId,
    offerId,
    positionId,
    { search: searchActive, limit: 50 },
    { enabled: open && positionId > 0 }
  );

  const { mutate: cloneCalculation, isLoading: isCloning } =
    usePostSalesCrmOfferCalculationFromTemplate(clientId, offerId, positionId, {
      onSuccess: (response) => {
        const data = (response.data ?? {}) as { id?: number; name?: string };
        const newId = data.id ?? 0;
        const newName = data.name ?? '';
        if (newId > 0) {
          enqueueSnackbar(t('salesCrm.calculation.template.cloneSuccess'), { variant: 'success' });
          onClonedSuccess({ id: newId, name: newName });
        }
      },
      onError: () => {
        enqueueSnackbar(t('salesCrm.calculation.template.cloneError'), { variant: 'error' });
      }
    });

  const related = data?.relatedToProduct ?? [];
  const other = data?.other ?? [];

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    setSearchActive(trimmed === '' ? undefined : trimmed);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchActive(undefined);
  };

  const handleSelectTemplate = (template: ICalculationTemplate) => {
    if (isCloning) return;
    cloneCalculation({ sourceCalculationId: template.id });
  };

  const renderTemplateRow = (template: ICalculationTemplate) => (
    <div key={template.id} className={styles.templateRow}>
      <div className={styles.templateMain}>
        <div className={styles.templateNameLine}>
          <span className={styles.templateName}>{template.name}</span>
          {template.isSameClient && (
            <span className={styles.sameClientBadge}>
              {t('salesCrm.calculation.template.sameClientBadge')}
            </span>
          )}
          {template.positions.length > 0 && (
            <Tooltip
              title={<TemplateTooltipBody positions={template.positions} />}
              arrow
              placement="top">
              <InfoOutlinedIcon fontSize="small" className={styles.infoIcon} />
            </Tooltip>
          )}
        </div>
        <div className={styles.templateMeta}>
          <span>
            <strong>{t('salesCrm.calculation.template.client')}:</strong>{' '}
            {template.clientName ?? '—'}
          </span>
          <span>
            <strong>{t('salesCrm.calculation.template.offer')}:</strong>{' '}
            {template.offerNumber ?? '—'}
          </span>
          <span>
            <strong>{t('salesCrm.calculation.template.createdAt')}:</strong>{' '}
            {formatDate(template.createdAt)}
          </span>
          <span>
            <strong>{t('salesCrm.calculation.template.valueNet')}:</strong>{' '}
            {formatPrice(template.valueNet)}
          </span>
        </div>
      </div>
      <div className={styles.templateActions}>
        <Button
          variant="contained"
          size="small"
          disabled={isCloning}
          onClick={() => handleSelectTemplate(template)}
          className={styles.primaryButton}>
          {t('salesCrm.calculation.template.select')}
        </Button>
      </div>
    </div>
  );

  return (
    <WiseModal
      open={open}
      onClose={onClose}
      title={t('salesCrm.calculation.template.title', { product: productName })}
      maxWidth={760}>
      <div className={styles.body}>
        <div className={styles.searchRow}>
          <TextField
            size="small"
            fullWidth
            value={searchInput}
            placeholder={t('salesCrm.calculation.template.searchPlaceholder')}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 6, color: '#999' }} />
            }}
          />
          <Button onClick={handleSearch} variant="outlined" size="small">
            {t('salesCrm.calculation.template.searchCta')}
          </Button>
          {searchActive && (
            <Button onClick={handleClearSearch} variant="text" size="small">
              {t('salesCrm.calculation.template.clearSearch')}
            </Button>
          )}
        </div>

        {isLoading && (
          <div className={styles.loading}>
            <CircularProgress size={24} />
          </div>
        )}

        {!isLoading && isError && (
          <div className={styles.error}>
            {t('salesCrm.calculation.template.loadError')}
          </div>
        )}

        {!isLoading && !isError && (
          <>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                {t('salesCrm.calculation.template.sectionRelated', { product: productName })}
              </div>
              {related.length === 0 ? (
                <div className={styles.empty}>
                  {searchActive
                    ? t('salesCrm.calculation.template.noRelatedMatchSearch')
                    : t('salesCrm.calculation.template.noRelated')}
                </div>
              ) : (
                related.map(renderTemplateRow)
              )}
            </div>

            {searchActive && other.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  {t('salesCrm.calculation.template.sectionOther')}
                </div>
                {other.map(renderTemplateRow)}
              </div>
            )}

            {searchActive && other.length === 0 && related.length === 0 && (
              <div className={styles.empty}>
                {t('salesCrm.calculation.template.noResults')}
              </div>
            )}
          </>
        )}

        <div className={styles.footer}>
          <Button onClick={onClose} variant="text" disabled={isCloning}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default ChooseCalculationTemplateModal;
